// SLOT 7.4 - the curated showcase photographs, as this section needs them.
//
// WHY THIS FILE EXISTS, WHICH IS A BUG REPORT AS MUCH AS A DATA FILE.
//
// /api/v2/orders/recent_completed splices the hand-picked frames into the feed
// so the strip is never empty (Order::LiveFeed::SHOWCASE_POSITIONS, every
// other slot from 5 to 23). It sends each one a `service`, an `area` and a
// photo path. It does NOT send the `alt` text, although
// Order::LiveFeed::SHOWCASE_TILES holds one for every tile
// (app/services/order/live_feed.rb). So the bundle has to recover it.
//
// Until today ./feed.js recovered it from ../hero/frames.js, which used to be
// a nine-entry mirror of the same Ruby constant. The hero was cut to five
// frames on 4 Oct. Nothing failed, because the recovery silently falls back to
// a generated description, so four of the nine showcase photographs quietly
// started shipping with "Photograph from a Mini Valet PLUS in E2, taken by the
// washer" in place of the sentence somebody wrote while looking at them. A
// section whose whole argument is that these photographs are real cannot
// describe half of them from a template, and it cannot be hostage to how many
// frames the fold happens to want this week.
//
// THE REAL FIX IS ONE LINE OF RUBY AND THIS FILE SHOULD THEN BE DELETED.
// `Order::LiveFeed.showcase_tiles` already has `t[:alt]` in hand; adding
// `alt: t[:alt]` to the hash it builds would let ./feed.js read the alt
// straight off the wire for showcase tiles, exactly as it reads the service
// and the area, and no mirror would exist at all. That is a file outside this
// slot's directory, so it is in the build report instead.
//
// UNTIL THEN: this is a verbatim mirror of Order::LiveFeed::SHOWCASE_TILES, in
// its order, service and area and alt unchanged, re-keyed by file name because
// that is what the photo path on the wire reduces to. Verified against the
// live endpoint on 4 Oct 2026, after gallery-image-03 was dropped server side.
// It is NOT asserted by spec/design/derived_values_spec.rb the way
// ../hero/frames.js is. If the Ruby fix above is not taken, this file needs
// adding to that assertion, which is also outside this directory and also in
// the report.
//
// Nothing here is marketing copy. The service and the postcode under a
// photograph are the only reason these beat a stock library, so they are
// copied and never written.
export const SHOWCASE_TILES = [
  {
    file: 'gallery-image-01.webp',
    service: 'Mini Valet',
    area: 'SW18',
    alt: 'A GoWashMyCar valeter in a hi-vis jacket at work on a foam-covered estate car beside the open doors of the van in Wandsworth'
  },
  {
    file: 'gallery-image-02.webp',
    service: 'Soft-top roof deep clean',
    area: 'NW1',
    alt: 'The fabric roof of a convertible after a soft-top deep clean in Camden'
  },
  {
    file: 'gallery-image-04.webp',
    service: 'Mini Valet PLUS',
    area: 'E2',
    alt: 'The same Mercedes before and after a Mini Valet PLUS in Bethnal Green'
  },
  {
    file: 'gallery-image-05.webp',
    service: 'Mini Valet PLUS',
    area: 'SE10',
    alt: 'A Mercedes SUV covered in white foam outside a brick terrace in Greenwich'
  },
  {
    file: 'gallery-image-06.webp',
    service: 'Exterior Wash & Wax',
    area: 'SW3',
    alt: 'Water beading on waxed paintwork, before and after an exterior wash and wax in Chelsea'
  },
  {
    file: 'gallery-image-07.webp',
    service: 'Mini Valet',
    area: 'N5',
    alt: 'A car under a full coat of white foam on a cobbled street in Highbury'
  },
  {
    file: 'gallery-image-08.webp',
    service: 'Alloy wheel & tyre deep clean',
    area: 'SW15',
    alt: 'A gloved hand scrubbing the spokes of an alloy wheel in Putney'
  },
  {
    file: 'gallery-image-09.webp',
    service: 'Engine bay deep clean',
    area: 'TW10',
    alt: 'An engine bay before and after a deep clean in Richmond'
  },
  {
    file: 'gallery-image-10.webp',
    service: 'Full Valet',
    area: 'SW19',
    alt: 'A gloved hand buffing a car panel dry in Wimbledon'
  }
]

// A SERVICE WE NO LONGER SELL CANNOT BE THE LABEL ON A PHOTOGRAPH.
//
// gallery-image-03 was captioned "Ultra Valet, W11" and the Ultra Valet was
// retired from the catalogue on 4 Oct 2026: /api/v2/services returns six base
// washes and twenty-eight extras and it is none of them. The caption was true
// the day that wash happened, which is precisely why the frame could not be
// quietly relabelled: the label is the evidence, and a label swapped for one
// that sells better is the single thing that would make this strip worth less
// than bought photography. ../hero/frames.js made that call for the fold and
// recorded that the matching decision for the feed "is a separate call"; it
// has since been made the same way in the Ruby, and the tile is gone from
// SHOWCASE_TILES above.
//
// This list survives that fix because the showcase ten were never the only
// place the problem can appear. Order::LiveFeed#tile_for labels a real wash
// with `order.services.find { |s| s.is_a?(Service::Base) }&.name`, read
// straight off the order, and there are ten years of orders behind this feed:
// the moment the photo backlog clears, a 2019 wash booked as an Ultra Valet
// can surface with that word under it. Held back rather than relabelled, for
// the same reason, and by name rather than by file so the server stays the
// place this is really fixed.
export const RETIRED_SERVICES = ['Ultra Valet']

// The order the curated frames run in when the feed is NOT live, which today
// is always: the newest publishable wash anywhere is 28 May 2026 (see
// ./feed.js for why). Keyed by file, because the server decides the running
// order of the real washes and only these are ours to arrange.
//
// It leads on the before-and-afters, and that is not a preference. They were
// cut out of the hero on 4 Oct with the reason written down in
// ../hero/frames.js: they are "proof, not hero", nobody ever scrolled far
// enough along the fold to reach them, and "§7.4 is a whole section of exactly
// this proof". This is §7.4. They belong at the front of it.
//
// They alternate with the work-in-progress frames rather than running three
// deep, because three split frames in a row read as one repeated graphic and
// because the visible row then carries both halves of the argument at once: at
// 1440 a visitor sees a before and after, then the van with its doors open,
// then a before and after, then snow foam on a Mercedes.
//
//   04  before and after, one Mercedes, Mini Valet PLUS
//   01  the operator, the van, the kit, the lance. The only frame we own with
//       a person, a van and the work in it
//   06  before and after, water beading on fresh wax
//   05  snow foam on a Merc against a London brick terrace
//   09  before and after, an engine bay
//   07  a car under full foam on a cobbled street
//   08  a gloved hand on the spokes of an alloy
//   10  a gloved hand buffing with a microfibre
//   02  last, and only because it is real: ../hero/frames.js calls it "a flat
//       grey soft-top that reads as nothing at size", which is a judgement
//       about prominence and not about honesty. It keeps its place in the
//       strip; it does not get a place at the front of it.
//
// A file missing from here sorts to the end rather than disappearing, so a new
// showcase photograph added to the Ruby shows up without an edit.
export const CURATED_ORDER = [
  'gallery-image-04.webp',
  'gallery-image-01.webp',
  'gallery-image-06.webp',
  'gallery-image-05.webp',
  'gallery-image-09.webp',
  'gallery-image-07.webp',
  'gallery-image-08.webp',
  'gallery-image-10.webp',
  'gallery-image-02.webp'
]
